// Панель, открытая кнопкой бота как мини-апп Telegram.
//
// Telegram передаёт мини-аппу подписанные данные запуска (initData) в хвосте
// адреса — #tgWebAppData=… . По ним сервер узнаёт, чей это Telegram, и сам
// впускает в привязанный аккаунт панели (/api/auth/telegram/webapp). Данные
// снимаются при загрузке модуля, пока роутер панели не переписал адрес, и
// живут в sessionStorage — перезагрузка страницы внутри Telegram их не теряет.

const INIT_KEY = "umbrella_tg_webapp_init";
const SESSION_KEY = "umbrella_tg_webapp_session";
const LOGGED_OUT_KEY = "umbrella_tg_webapp_logged_out";
const KEYBOARD_KEY = "umbrella_tg_webapp_keyboard";
const SCRIPT_URL = "https://telegram.org/js/telegram-web-app.js";

const readSession = (key) => {
  try {
    return window.sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeSession = (key, value) => {
  try {
    if (value) window.sessionStorage.setItem(key, value);
    else window.sessionStorage.removeItem(key);
  } catch {
    // Хранилище недоступно — вход просто не переживёт перезагрузку.
  }
};

const capturedInitData = (() => {
  if (typeof window === "undefined") return "";
  const hash = String(window.location.hash || "").replace(/^#/, "");
  let fromHash = "";
  if (hash.includes("tgWebAppData=")) {
    try {
      fromHash = new URLSearchParams(hash).get("tgWebAppData") || "";
    } catch {
      fromHash = "";
    }
  }
  if (fromHash) writeSession(INIT_KEY, fromHash);
  // Telegram открыл панель, но данных запуска не дал — так бывает с кнопки
  // клавиатуры бота (у кого она ещё старая, web_app). См. requestPanelButtonFromBot.
  if (/(^|&)tgWebAppPlatform=/.test(hash)) writeSession(KEYBOARD_KEY, fromHash ? "" : "1");
  return fromHash || readSession(INIT_KEY);
})();

// Метка на <html> для стилей мини-аппа (см. index.css, «Мини-апп Telegram»).
if (capturedInitData && typeof document !== "undefined") {
  document.documentElement.classList.add("tg-webapp");
}

export const getTelegramInitData = () => (
  (typeof window !== "undefined" && window.Telegram?.WebApp?.initData) || capturedInitData
);

export const isTelegramMiniApp = () => Boolean(getTelegramInitData());

// Сессия из ответа входа — для запросов заголовком x-auth-session. Нужна
// в Telegram Web: там мини-апп во фрейме чужого сайта, и cookie панели туда
// может не попасть. В приложениях Telegram хватает и cookie.
export const getMiniAppSession = () => (isTelegramMiniApp() ? readSession(SESSION_KEY) : "");
export const setMiniAppSession = (sessionId) => writeSession(SESSION_KEY, String(sessionId || ""));

// Вышел сам — до закрытия мини-аппа обратно не впускаем: иначе первая же
// перезагрузка страницы снова открыла бы его аккаунт.
export const markMiniAppLoggedOut = () => {
  writeSession(SESSION_KEY, "");
  writeSession(LOGGED_OUT_KEY, "1");
};
export const wasMiniAppLoggedOut = () => readSession(LOGGED_OUT_KEY) === "1";

export const isKeyboardLaunchedMiniApp = () => !isTelegramMiniApp() && readSession(KEYBOARD_KEY) === "1";

let webAppPromise = null;
const loadWebApp = () => {
  if (!webAppPromise) {
    webAppPromise = new Promise((resolve) => {
      if (window.Telegram?.WebApp) {
        resolve(window.Telegram.WebApp);
        return;
      }
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve(window.Telegram?.WebApp || null);
      script.onerror = () => resolve(null);
      document.head.appendChild(script);
    });
  }
  return webAppPromise;
};

// Открыта кнопкой клавиатуры бота: войти сама панель не может, данных запуска
// нет. Она просит бота (sendData — только у таких запусков) прислать кнопку
// под сообщением, с которой данные будут, и Telegram закрывает мини-апп. Не
// закрыл (старый клиент) — панель продолжает как обычно, со входом по паролю.
export const requestPanelButtonFromBot = async () => {
  if (!isKeyboardLaunchedMiniApp() || typeof document === "undefined") return;
  const webApp = await loadWebApp();
  if (!webApp || typeof webApp.sendData !== "function" || webApp.initData || webApp.platform === "unknown") return;
  try {
    webApp.sendData("open_panel");
  } catch {
    return;
  }
  await new Promise((resolve) => setTimeout(resolve, 2000));
};

// Шапка и фон окна Telegram — цветом полотна панели (--ui-page), чтобы
// мини-апп не выглядел вставкой в чужое окно.
export const syncMiniAppColors = () => {
  if (!isTelegramMiniApp() || typeof document === "undefined") return;
  loadWebApp().then((webApp) => {
    if (!webApp?.isVersionAtLeast?.("6.1")) return;
    const color = getComputedStyle(document.documentElement).getPropertyValue("--ui-page").trim();
    if (!/^#[0-9a-f]{6}$/i.test(color)) return;
    try {
      webApp.setHeaderColor(color);
      webApp.setBackgroundColor(color);
    } catch {
      // Старый клиент Telegram — останутся его цвета.
    }
  });
};

export const initTelegramMiniApp = () => {
  if (!isTelegramMiniApp() || typeof document === "undefined") return;
  loadWebApp().then((webApp) => {
    if (!webApp) return;
    try {
      webApp.ready();
      webApp.expand();
      // Свайп вниз по панели листает её, а не сворачивает мини-апп.
      if (webApp.isVersionAtLeast?.("7.7")) webApp.disableVerticalSwipes();
    } catch {
      // Ничего страшного: панель работает и без этих удобств.
    }
    syncMiniAppColors();
  });
};
